import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Yup from 'yup';
import { Formik, useFormikContext } from 'formik';
import { Button, Grid } from '@material-ui/core';
import { FarmRiskType, lookupsJson } from '@ourbranch/lookups';

import { getPropertyTypeOptions, getResidenceUsageOptions } from 'common/helpers/farm-helpers';
import { useDisabledState } from 'common/disabled-context';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import { NotificationCard } from 'core/components/notification-card';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import useStyles from './risk.styles';

const riskTypeOptions = lookupsJson.farm.riskType.filter(
  (option) => ![FarmRiskType.Fo6Scheduled, FarmRiskType.Fo6Unscheduled].includes(option.id)
);

// FO-4 property type options come from getPropertyTypeOptions (locked to FD "Home or Rental Unit")

const validationSchema = () =>
  Yup.object().shape({
    description: Yup.string().required('Risk Name is required'),
    type: Yup.string().required('Dwelling or structure type is required'),
    building: Yup.object().when('type', {
      is: (value) => [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(value),
      then: (schema) =>
        schema.shape({
          propertyType: Yup.string().required('Structure Category is required'),
          residenceUsage: Yup.string().required('Residence Type/Usage is required')
        }),
      otherwise: (schema) =>
        schema.shape({
          propertyType: Yup.string(),
          residenceUsage: Yup.string()
        })
    })
  });

// Auto-set FO4 defaults (locked fields) when risk type is selected — GitHub Issue: #29021
const FO4DefaultsSetter = () => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.type === FarmRiskType.Fo4) {
      if (values.building?.propertyType !== 'FD') {
        setFieldValue('building.propertyType', 'FD');
      }
      if (values.building?.residenceUsage !== 'C') {
        setFieldValue('building.residenceUsage', 'C');
      }
    }
  }, [values.type, setFieldValue]);

  return null;
};

// Wrapper component to access parent Formik context and validation modal
const AddRiskWithValidation = observer(({ onAdd, locationAddress, disabled, fieldName }) => {
  const parentFormik = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  return (
    <>
      <AddRiskInternal
        onAdd={onAdd}
        locationAddress={locationAddress}
        disabled={disabled}
        fieldName={fieldName}
        parentValidateForm={parentFormik.validateForm}
        parentSetErrors={parentFormik.setErrors}
        parentSetTouched={parentFormik.setTouched}
        setShowValidationErrorsModal={setShowValidationErrorsModal}
      />
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
    </>
  );
});

const AddRiskInternal = observer(
  ({
    onAdd,
    locationAddress,
    disabled,
    fieldName,
    parentValidateForm,
    parentSetErrors,
    parentSetTouched,
    setShowValidationErrorsModal
  }) => {
    const classes = useStyles();
    const { activeForm } = useDisabledState();

    const disableForm = Boolean(activeForm || disabled);

    // Structure categories depend on state, so we need to get them dynamically
    const structureCategories = locationAddress?.state
      ? lookupsJson.farm.structureCategoriesByState[locationAddress.state]
      : [];

    return (
      <Formik
        initialValues={{
          description: '',
          type: undefined,
          propertyType: undefined,
          adjustmentFactor: 0,
          building: {
            buildingType: undefined,
            propertyType: undefined,
            residenceUsage: undefined,
            constructionType: undefined,
            numSolidFuelBurningAppliances: undefined
          }
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          // First validate the parent form (entire offer/policy)
          const parentErrors = await parentValidateForm();
          if (Object.keys(parentErrors).length > 0) {
            // Set errors and show validation modal
            parentSetErrors(parentErrors);
            // Convert errors object to touched structure (boolean flags instead of error messages)
            const touched = setTouchedFromErrors(parentErrors);
            parentSetTouched(touched);
            setShowValidationErrorsModal(true);
            return;
          }

          // If parent validation passes, proceed with location/risk-specific validation
          // Validate that location has required address fields
          if (!locationAddress?.city || !locationAddress?.state || !locationAddress?.zip) {
            actions.setStatus({
              error:
                'Location must have a complete address (at least City, State, and Zip) before adding risks. Please complete the location address first.'
            });
            return;
          }

          // For street addresses, also require street address
          if (
            locationAddress?.address === undefined &&
            values.type &&
            [
              FarmRiskType.Fo1,
              FarmRiskType.Fo2,
              FarmRiskType.Fo3,
              FarmRiskType.Fo4,
              FarmRiskType.Fo6Structures
            ].includes(values.type)
          ) {
            actions.setStatus({
              error:
                'This risk type requires a complete street address. Please ensure the location has a street address.'
            });
            return;
          }

          // Clear any previous errors
          actions.setStatus({});

          onAdd({
            ...values
          });
          actions.resetForm();
        }}
      >
        {({ values, submitForm, status }) => {
          return (
            <div className={classes.container}>
              <Label type="infoSubtitleSmall">Add a dwelling or structure</Label>
              {status?.error && (
                <NotificationCard className={classes.notification} iconSize="icon16" type="errorRed">
                  <Label>{status.error}</Label>
                </NotificationCard>
              )}
              <Grid key="container" container justifyContent="space-between" alignItems="center" spacing={2}>
                <Field
                  xs={6}
                  type="select"
                  id={`${fieldName}-type`}
                  name="type"
                  mode="light"
                  label="Risk Type*"
                  options={riskTypeOptions}
                  disabled={disableForm}
                />
                <Field
                  xs={6}
                  type="string"
                  id={`${fieldName}-description`}
                  name="description"
                  mode="light"
                  label="Risk Name*"
                  disabled={disableForm}
                />

                {values.type === FarmRiskType.Fo6Structures && locationAddress?.state && (
                  <Field
                    xs={12}
                    type="select"
                    id={`${fieldName}-building.buildingType`}
                    name="building.buildingType"
                    mode="light"
                    label="Structure Category"
                    options={structureCategories}
                    disabled={disableForm}
                  />
                )}

                {[FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(values.type) && (
                  <>
                    <Field
                      xs={6}
                      type="select"
                      id={`${fieldName}.building.propertyType`}
                      name="building.propertyType"
                      mode="light"
                      label="Structure Category*"
                      options={getPropertyTypeOptions(values.type)}
                      disabled={disableForm}
                    />
                    <Field
                      xs={6}
                      type="select"
                      id={`${fieldName}.building.residenceUsage`}
                      name="building.residenceUsage"
                      mode="light"
                      label="Residence Type/Usage*"
                      options={getResidenceUsageOptions(values.type)}
                      disabled={disableForm}
                    />
                  </>
                )}

                {values.type === FarmRiskType.Fo4 && <FO4DefaultsSetter />}

                {/* Address automatically inherits from location */}
                {values.type === FarmRiskType.Fo4 && (
                  <Grid item xs={12} className={classes.rentersInfoTextContainer}>
                    <InfoIcon width={16} height={16} />
                    <span className={classes.rentersInfoText}>
                      Make sure to include a floor, unit, or building number if one exists for this rental.
                    </span>
                  </Grid>
                )}
                <Grid item container justifyContent="flex-end" xs={12}>
                  <Button
                    type="button"
                    key="addButton"
                    mode="big"
                    variant="contained"
                    color="secondary"
                    className={classes.smallButton}
                    disabled={disableForm}
                    onClick={submitForm}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
              {disableForm && (
                <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
                  <Label>You can't add a dwelling or structure, or any risk, until you update the offer.</Label>
                </NotificationCard>
              )}
            </div>
          );
        }}
      </Formik>
    );
  }
);

AddRiskWithValidation.propTypes = {
  onAdd: PropTypes.func.isRequired,
  locationAddress: PropTypes.shape({
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired
  }),
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired
};

AddRiskWithValidation.defaultProps = {
  disabled: false
};

AddRiskInternal.propTypes = {
  onAdd: PropTypes.func.isRequired,
  locationAddress: PropTypes.shape({
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired
  }),
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  parentValidateForm: PropTypes.func.isRequired,
  parentSetErrors: PropTypes.func.isRequired,
  parentSetTouched: PropTypes.func.isRequired,
  setShowValidationErrorsModal: PropTypes.func.isRequired
};

export default AddRiskWithValidation;
